import { isAxiosError } from 'axios';

/**
 * Normalized API error — the ONLY error shape stores/components consume.
 *
 * The backends have no shared error envelope (docs/05a-api-baseline.md §2).
 * Every verified variant folds into this one shape:
 *
 *   { error: "..." }                             middleware, login 400, barcode 404
 *   { error: "...", message: "..." }             pos.terminal middleware 403/404
 *   { message: "..." }                           Passport 401, findOrFail 404
 *   { message: "...", errors: {field: [..]} }    Laravel 422 (login, orders validation)
 *   { success:false, message: "...", errors? }   day-sessions 422/400
 *   { success:false, errors: {field: [..]} }     catalog (product/customer/brand) 422
 *   { success:false, error: "..." }              order-create/status 400 business rejections
 *   { success:false, error: {code, message} }    order details 404
 */
export interface ApiError {
  /** HTTP status; 0 when the request never reached the server (network/CORS/abort). */
  status: number;
  message: string;
  /** Laravel-style validation errors keyed by field. */
  fieldErrors?: Record<string, string[]>;
  /** Machine code when the envelope carried one (e.g. ORDER_NOT_FOUND). */
  code?: string;
}

/**
 * Patterns that mean the backend leaked something a cashier must never see.
 *
 * A Laravel app with `APP_DEBUG=true` answers a QueryException with the whole
 * statement in `message` — that is how "SQLSTATE[42S22]: Column not found:
 * 1054 Unknown column 'organization_user.is_active' ... select exists(select *
 * from `users` inner join ...)" reached the register screen, three panels at
 * once. The handover forbids exactly that class of output: no raw SQL, stack
 * traces, tokens, internal URLs or request bodies in the UI.
 *
 * This list is deliberately about SHAPE, not about specific errors. A new
 * backend defect must fail safe by default rather than wait for someone to add
 * a pattern for it.
 */
const LEAK_PATTERNS: RegExp[] = [
  /SQLSTATE\[/i, //            PDO / QueryException
  /\bSQL:\s/i, //              the statement Laravel appends to the message
  /\b(?:select|insert into|update|delete from|alter table|drop table)\b[\s\S]*\bfrom\b|\binner join\b/i,
  /\bPDOException\b|\bQueryException\b/i,
  /(?:Illuminate|Symfony|Laravel|Doctrine|App)\\[A-Za-z]/, // internal class paths
  /Stack trace:|^#\d+\s|\s#\d+\s\/|\.php(?::\d+| on line \d+)/im, // traces & file refs
  /\b(?:Bearer\s+[\w-]{8,}|eyJ[\w-]{10,})/, //  tokens
  /https?:\/\/|\b[A-Za-z]:\\|\/var\/www\/|\/home\/\w+\//, // internal URLs & paths
  /Connection:\s*\w+,/i, //    Laravel's "(Connection: mysql_main, SQL: ...)"
];

/**
 * Longest message we will show verbatim. Real operator-facing copy from these
 * backends is a sentence; anything far past that is a dump, not a message.
 */
const MAX_SAFE_MESSAGE = 240;

/** Generic, actionable copy per status — used whenever the real message is unsafe. */
function fallbackMessage(status: number): string {
  if (status === 0) return 'Network error — could not reach the server';
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'That item could not be found.';
  if (status === 409) return 'That changed while you were working. Refresh and try again.';
  if (status === 422) return 'Some details are not valid. Check the highlighted fields.';
  if (status === 429) return 'Too many attempts. Wait a moment and try again.';
  if (status >= 500) return 'The server could not complete that. Try again, or retry in a moment.';
  return 'That request could not be completed.';
}

/**
 * Return `raw` when it is safe to show an operator, otherwise safe copy.
 *
 * Exported so the same rule can be asserted directly in tests and reused by any
 * surface that renders a backend string it did not route through
 * `normalizeApiError`.
 */
export function toSafeMessage(raw: unknown, status: number, fallback?: string): string {
  const safeFallback = fallback ?? fallbackMessage(status);
  if (typeof raw !== 'string') return safeFallback;

  const message = raw.trim();
  if (!message) return safeFallback;

  // "No query results for model [App\Models\Product] 999" — a real 404, but it
  // names an internal model. Answer the operator's actual question instead.
  if (/^No query results for model\b/i.test(message)) return fallbackMessage(404);

  if (message.length > MAX_SAFE_MESSAGE) return safeFallback;
  if (LEAK_PATTERNS.some((pattern) => pattern.test(message))) return safeFallback;

  return message;
}

function toFieldErrors(raw: unknown): Record<string, string[]> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const msgs = value.filter((v): v is string => typeof v === 'string');
      if (msgs.length > 0) out[key] = msgs;
    } else if (typeof value === 'string') {
      out[key] = [value];
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function firstFieldError(fieldErrors: Record<string, string[]> | undefined): string | null {
  if (!fieldErrors) return null;
  for (const msgs of Object.values(fieldErrors)) {
    if (msgs.length > 0) return msgs[0] ?? null;
  }
  return null;
}

/**
 * A zod parse failure, told apart from every other thrown value.
 *
 * Structurally rather than with `instanceof ZodError`: this module is imported
 * by the stores and must not pull zod into their chunk, and an `issues` array
 * of `{ path, message }` is zod's own public shape.
 *
 * It needs its own branch because a ZodError's `.message` is a pretty-printed
 * JSON array — hundreds of characters — so `toSafeMessage` rejects it on length
 * (MAX_SAFE_MESSAGE) and every schema mismatch in the application reported
 * itself as the same three words: "Unknown error". Store settings was down on
 * one tenant for exactly this reason and the message named neither the endpoint
 * nor the field; the path below is what turns the next one into a lead.
 */
function zodIssuePath(e: unknown): string | null {
  if (!e || typeof e !== 'object') return null;
  const issues = (e as { issues?: unknown }).issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const first = issues[0] as { path?: unknown; message?: unknown };
  if (typeof first?.message !== 'string') return null;
  const path = Array.isArray(first.path) ? first.path.join('.') : '';
  return path ? `${path}: ${first.message}` : first.message;
}

/** Fold any thrown value (axios or otherwise) into a normalized ApiError. */
export function normalizeApiError(e: unknown): ApiError {
  if (isApiError(e)) return e;

  if (isAxiosError(e)) {
    if (!e.response) {
      // Request cancelled or never reached the server.
      return { status: 0, message: 'Network error — could not reach the server' };
    }
    const status = e.response.status;
    const body: unknown = e.response.data;

    if (body && typeof body === 'object' && !Array.isArray(body)) {
      const b = body as Record<string, unknown>;
      const fieldErrors = toFieldErrors(b.errors);

      // { success:false, error: {code, message} }
      if (b.error && typeof b.error === 'object' && !Array.isArray(b.error)) {
        const nested = b.error as Record<string, unknown>;
        return {
          status,
          message: toSafeMessage(nested.message, status),
          ...(typeof nested.code === 'string' ? { code: nested.code } : {}),
          ...(fieldErrors ? { fieldErrors } : {}),
        };
      }

      // { error: "..." } — optionally with a human "message" sibling (terminal middleware)
      if (typeof b.error === 'string') {
        const message = typeof b.message === 'string' ? b.message : b.error;
        return {
          status,
          message: toSafeMessage(message, status),
          ...(fieldErrors ? { fieldErrors } : {}),
        };
      }

      // { message: "..." } with or without errors, success flag irrelevant
      if (typeof b.message === 'string') {
        return {
          status,
          message: toSafeMessage(b.message, status),
          ...(fieldErrors ? { fieldErrors } : {}),
        };
      }

      // { success:false, errors: {...} } — catalog 422 has NO message key
      if (fieldErrors) {
        return {
          status,
          message: toSafeMessage(firstFieldError(fieldErrors), status),
          fieldErrors,
        };
      }
    }

    // Axios' own "Request failed with status code 500" carries no server detail,
    // but it is developer copy, not operator copy.
    return { status, message: fallbackMessage(status) };
  }

  /*
    A SCHEMA FAILURE IS NOT OPERATOR COPY.

    A ZodError is an Error, and its `message` is the serialized issue list. So
    the branch below used to put a raw JSON blob on screen — observed on
    /setup, where one terminal whose `settings` had never been written came
    back as `[]` and the whole list failed to parse:

      [{"expected":"object","code":"invalid_type",
        "path":["data",1,"settings"],"message":"Invalid input: …"}]

    A person standing at a till cannot act on that, and it is the console
    describing its own internals to a merchant. The response genuinely did not
    match the contract, so it stays an error — it just says so in words, and
    the detail goes to the console for whoever is debugging.

    Detected structurally rather than with `instanceof z.ZodError`: this module
    is imported by everything and has no other reason to depend on zod, and the
    shape is stable across zod versions in a way the class identity is not
    (two copies of zod in a tree defeat instanceof).
  */
  if (isSchemaError(e)) {
    if (import.meta.env?.DEV) console.error('Response did not match the expected shape:', e);
    return {
      status: 0,
      code: 'schema_mismatch',
      message: 'The server sent something this screen could not read. Try again, or report it if it keeps happening.',
    };
  }

  // Status 0 here means "never became an HTTP exchange at all", which is NOT the
  // same as a network failure — so it keeps its own fallback rather than
  // fallbackMessage(0)'s "could not reach the server".
  // The response arrived and did not match the schema — a different failure
  // from "the server is down", and the only one the operator cannot retry out
  // of. Say so, and log the field for whoever has to fix it.
  const issue = zodIssuePath(e);
  if (issue !== null) {
    console.error('[api] response did not match its schema —', issue, e);
    return { status: 0, message: 'The server sent something this page could not read' };
  }

  return {
    status: 0,
    message: toSafeMessage(e instanceof Error ? e.message : null, 0, 'Unknown error'),
  };
}

/** A zod parse failure, identified by shape rather than by class identity. */
function isSchemaError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const candidate = e as { name?: unknown; issues?: unknown };
  return candidate.name === 'ZodError' || Array.isArray(candidate.issues);
}

/** True when the value already looks like a normalized ApiError. */
export function isApiError(e: unknown): e is ApiError {
  return (
    !!e &&
    typeof e === 'object' &&
    typeof (e as ApiError).status === 'number' &&
    typeof (e as ApiError).message === 'string' &&
    !(e instanceof Error)
  );
}
